<template>
  <div class="workflow-designer">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb">
      <router-link to="/" class="breadcrumb-item">
        <i class="fas fa-home"></i>
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item active">Visual Workflow Designer</span>
    </div>

    <!-- Header Section -->
    <div class="header-section">
      <h2>🎯 Visual Workflow Designer</h2>
      <p class="description">
        Build sophisticated compliance workflows visually using drag-and-drop. 
        Connect automated checks into comprehensive analysis pipelines.
      </p>
    </div>

    <!-- Getting Started Guide -->
    <div class="getting-started-section">
      <div class="intro-card">
        <h3>🎨 Visual Workflow Creation</h3>
        <p>
          The Visual Workflow Designer lets you build complex compliance workflows by connecting individual automated checks
          into comprehensive analysis pipelines with complete visual control.
        </p>
        <div class="benefits-grid">
          <div class="benefit-item">
            <i class="fas fa-mouse-pointer"></i>
            <strong>Drag & Drop</strong>
            <p>Intuitive visual interface for building workflows without coding</p>
          </div>
          <div class="benefit-item">
            <i class="fas fa-project-diagram"></i>
            <strong>Visual Flow</strong>
            <p>See your entire workflow at a glance with connected components</p>
          </div>
          <div class="benefit-item">
            <i class="fas fa-puzzle-piece"></i>
            <strong>Modular Checks</strong>
            <p>Combine existing automated checks into powerful workflows</p>
          </div>
          <div class="benefit-item">
            <i class="fas fa-play-circle"></i>
            <strong>Live Preview</strong>
            <p>Test and preview your workflow logic before deployment</p>
          </div>
        </div>
      </div>

      <div class="how-to-card">
        <h3>🛠️ How to Build Your Visual Workflow</h3>
        <div class="steps-list">
          <div class="step">
            <span class="step-number">1</span>
            <div class="step-content">
              <strong>Drag Checks from Tools Panel</strong>
              <p>Browse available automated checks in the left panel and drag them onto the canvas</p>
            </div>
          </div>
          <div class="step">
            <span class="step-number">2</span>
            <div class="step-content">
              <strong>Connect Components</strong>
              <p>Link checks together to create analysis flows and decision trees</p>
            </div>
          </div>
          <div class="step">
            <span class="step-number">3</span>
            <div class="step-content">
              <strong>Configure Properties</strong>
              <p>Click on components to configure their settings, prompts, and validation rules</p>
            </div>
          </div>
          <div class="step">
            <span class="step-number">4</span>
            <div class="step-content">
              <strong>Test & Deploy</strong>
              <p>Preview your workflow and save it as a template for document analysis</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message Display -->
    <div v-if="error" class="error-message">
      <h3>❌ Designer Error</h3>
      <p>We encountered an issue loading the workflow designer. Please refresh the page or try again later.</p>
      <pre>{{ error }}</pre>
    </div>

    <div class="designer-container">
      <!-- Tools Panel -->
      <div class="tools-panel">
        <div class="tools-header">
          <h3>🧰 Available Checks</h3>
          <p class="tools-description">
            Drag any check onto the canvas to add it to your workflow
          </p>
        </div>
        
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Loading checks...</span>
          </div>
        </div>
        
        <div v-else-if="error" class="error-state">
          <div class="error-content">
            <i class="fas fa-exclamation-triangle"></i>
            <span>Failed to load checks</span>
            <button @click="fetchAutomatedChecks" class="retry-btn">
              <i class="fas fa-redo"></i> Retry
            </button>
          </div>
        </div>
        
        <div v-else class="workflow-steps">
          <div class="steps-count">
            <i class="fas fa-check-circle"></i>
            <span>{{ automatedChecks.length }} checks available</span>
          </div>
          
          <div class="drag-hint">
            <i class="fas fa-hand-pointer"></i>
            <span>Drag checks to the canvas below</span>
          </div>
          
          <div 
            v-for="check in automatedChecks" 
            :key="check.id"
            class="workflow-step-item"
            draggable="true"
            @dragstart="dragStart($event, check)"
          >
            <div class="step-icon">
              <i :class="getStepIcon(check.check_type)"></i>
            </div>
            <div class="step-info">
              <span class="step-name">{{ check.name }}</span>
              <span class="step-type">{{ check.check_type }}</span>
            </div>
            <div class="drag-indicator">
              <i class="fas fa-grip-vertical"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Canvas -->
      <div class="workflow-canvas">
        <div class="canvas-header">
          <h3>🎨 Workflow Canvas</h3>
          <div class="canvas-controls">
            <button @click="clearCanvas" class="btn btn-outline">
              <i class="fas fa-trash"></i> Clear Canvas
            </button>
            <button @click="saveWorkflow" class="btn btn-primary" :disabled="elements.length === 0">
              <i class="fas fa-save"></i> Save Workflow
            </button>
          </div>
        </div>
        
        <div class="canvas-container">
          <div v-if="elements.length === 0" class="empty-canvas">
            <div class="empty-canvas-content">
              <i class="fas fa-arrow-left"></i>
              <h4>Start Building Your Workflow</h4>
              <p>Drag automated checks from the left panel onto this canvas to begin creating your workflow.</p>
              <div class="canvas-tips">
                <div class="tip">
                  <i class="fas fa-lightbulb"></i>
                  <span>Tip: You can connect checks by dragging from output ports to input ports</span>
                </div>
                <div class="tip">
                  <i class="fas fa-mouse-pointer"></i>
                  <span>Click on any check to configure its properties and prompts</span>
                </div>
              </div>
            </div>
          </div>
          
          <VueFlow 
            v-model="elements" 
            :default-viewport="{ x: 0, y: 0, zoom: 1 }"
            @dragover="onDragOver"
            @drop="onDrop"
            class="vue-flow-canvas"
            ref="vueFlow"
            @nodeClick="onNodeClick"
          >
            <template #node-custom="nodeProps">
              <WorkflowStepNode 
                v-bind="nodeProps"
                :check="getCheckDetails(nodeProps.data.checkId)"
              />
            </template>
            
            <Background pattern-color="#aaa" :gap="8" />
            <Controls />
            <MiniMap />
          </VueFlow>
        </div>
      </div>
    </div>

    <!-- Properties Modal -->
    <Modal v-if="selectedNode" @close="closeProperties">
      <template #header>
        <h3>Step Properties</h3>
      </template>
      <template #body>
        <div class="properties-content">
          <div v-if="selectedCheck">
            <h4>{{ selectedCheck.name }}</h4>
            <p>{{ selectedCheck.description }}</p>
            <div class="check-details">
              <div class="severity">
                Severity: {{ selectedCheck.severity }}
              </div>
              <div class="prompt">
                <h5>Prompt Template:</h5>
                <pre>{{ selectedCheck.prompt_template }}</pre>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { VueFlow } from '@vue-flow/core'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import { MiniMap } from '@vue-flow/minimap'
import WorkflowStepNode from './workflow-steps/WorkflowStepNode.vue'
import Modal from './Modal.vue'
import { ref, onMounted } from 'vue'
import api from '@/services/api'

export default {
  name: 'WorkflowDesigner',
  components: {
    VueFlow,
    Background,
    Controls,
    MiniMap,
    WorkflowStepNode,
    Modal
  },
  setup() {
    const elements = ref([])
    const automatedChecks = ref([])
    const selectedNode = ref(null)
    const selectedCheck = ref(null)
    const loading = ref(false)
    const error = ref(null)

    const fetchAutomatedChecks = async () => {
      loading.value = true
      error.value = null
      try {
        const response = await api.get('/automated-checks/')
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        automatedChecks.value = response.data
      } catch (err) {
        console.error('Error fetching automated checks:', err)
        error.value = `Failed to load automated checks: ${err.message}`
        automatedChecks.value = []
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchAutomatedChecks()
    })

    return {
      elements,
      automatedChecks,
      selectedNode,
      selectedCheck,
      loading,
      error
    }
  },
  methods: {
    getStepIcon(type) {
      const icons = {
        'ANALYSIS': 'fas fa-search',
        'VALIDATION': 'fas fa-check-circle',
        'COMPLIANCE': 'fas fa-shield-alt',
        'CUSTOM': 'fas fa-cog'
      }
      return icons[type] || 'fas fa-question'
    },
    dragStart(event, check) {
      event.dataTransfer.setData('application/vueflow', JSON.stringify(check))
      event.dataTransfer.effectAllowed = 'move'
    },
    onDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },
    onDrop(event) {
      const check = JSON.parse(event.dataTransfer.getData('application/vueflow'))
      const position = this.$refs.vueFlow.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })
      this.addNode(check, position)
    },
    addNode(check, position) {
      const newNode = {
        id: `node-${Date.now()}`,
        type: 'custom',
        position,
        data: { 
          label: check.name,
          checkId: check.id,
          type: check.check_type
        }
      }
      this.elements = [...this.elements, newNode]
    },
    onNodeClick(event, node) {
      this.selectedNode = node
      this.selectedCheck = this.getCheckDetails(node.data.checkId)
    },
    getCheckDetails(checkId) {
      return this.automatedChecks.find(check => check.id === checkId)
    },
    closeProperties() {
      this.selectedNode = null
      this.selectedCheck = null
    },
    clearCanvas() {
      if (this.elements.length > 0 && confirm('Are you sure you want to clear the entire canvas? This action cannot be undone.')) {
        this.elements = []
      }
    },
    async saveWorkflow() {
      if (this.elements.length === 0) {
        alert('Please add some checks to your workflow before saving.')
        return
      }
      
      const workflowName = prompt('Enter a name for your workflow:')
      if (!workflowName) return
      
      try {
        // TODO: Implement actual save functionality
        const workflowData = {
          name: workflowName,
          description: `Visual workflow with ${this.elements.length} components`,
          elements: this.elements
        }
        
        // Placeholder for actual API call
        console.log('Saving workflow:', workflowData)
        alert('Workflow saved successfully! (Note: This is a demo - actual save functionality needs to be implemented)')
        
      } catch (error) {
        console.error('Error saving workflow:', error)
        alert('Failed to save workflow. Please try again.')
      }
    }
  }
}
</script>

<style scoped>
.workflow-designer {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
}

/* Breadcrumb */
.breadcrumb {
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.breadcrumb-item {
  color: #6b7280;
  text-decoration: none;
  padding: 5px 8px;
  border-radius: 4px;
  transition: all 0.2s;
}

.breadcrumb-item:hover {
  color: #374151;
  background: #f3f4f6;
}

.breadcrumb-item.active {
  color: #111827;
  font-weight: 600;
  background: #e3f2fd;
}

.breadcrumb-separator {
  color: #9ca3af;
}

/* Header section styling */
.header-section {
  padding: 20px;
  text-align: center;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.header-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 2.2rem;
}

.description {
  color: #7f8c8d;
  font-size: 16px;
  max-width: 800px;
  margin: 0 auto;
}

/* Getting Started Section */
.getting-started-section {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.intro-card, .how-to-card {
  background: white;
  padding: 25px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid #3498db;
}

.intro-card h3, .how-to-card h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 18px;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.benefit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.benefit-item i {
  font-size: 2em;
  color: #3498db;
  margin-bottom: 10px;
}

.benefit-item strong {
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 14px;
}

.benefit-item p {
  margin: 0;
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.4;
}

/* Steps list styling */
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.step-number {
  width: 30px;
  height: 30px;
  background: #3498db;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content strong {
  color: #2c3e50;
  margin-bottom: 5px;
  display: block;
}

.step-content p {
  margin: 0;
  color: #7f8c8d;
  line-height: 1.4;
}

/* Error message */
.error-message {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-left: 4px solid #e53e3e;
  border-radius: 6px;
  padding: 20px;
  margin: 20px;
}

.error-message h3 {
  color: #c53030;
  margin-bottom: 10px;
}

.error-message p {
  color: #742a2a;
  margin-bottom: 15px;
}

.error-message pre {
  background: #fed7d7;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  color: #742a2a;
}

/* Designer container */
.designer-container {
  flex: 1;
  display: flex;
  gap: 20px;
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

/* Tools Panel */
.tools-panel {
  width: 300px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid #27ae60;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 300px);
}

.tools-header {
  padding: 20px;
  border-bottom: 1px solid #ecf0f1;
}

.tools-header h3 {
  color: #2c3e50;
  margin: 0 0 8px 0;
  font-size: 18px;
}

.tools-description {
  color: #7f8c8d;
  margin: 0;
  font-size: 14px;
}

.loading-state, .error-state {
  padding: 20px;
  text-align: center;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #7f8c8d;
}

.loading-spinner i {
  font-size: 24px;
  color: #3498db;
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #e74c3c;
}

.retry-btn {
  background: #e74c3c;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.retry-btn:hover {
  background: #c0392b;
}

.workflow-steps {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px 20px;
}

.steps-count {
  background: #e8f8f5;
  color: #27ae60;
  padding: 10px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
}

.drag-hint {
  background: #f8f9fa;
  color: #6c757d;
  padding: 10px 15px;
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  border: 1px dashed #dee2e6;
}

.workflow-step-item {
  padding: 15px;
  background: #fdfdfd;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: move;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  transition: all 0.2s;
}

.workflow-step-item:hover {
  background: #f8f9fa;
  border-color: #3498db;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.1);
}

.step-icon {
  width: 32px;
  height: 32px;
  background: #3498db;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  line-height: 1.2;
}

.step-type {
  font-size: 12px;
  color: #7f8c8d;
  text-transform: uppercase;
  font-weight: 500;
}

.drag-indicator {
  color: #bdc3c7;
  font-size: 12px;
}

/* Canvas */
.workflow-canvas {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid #9b59b6;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.canvas-header {
  padding: 20px;
  border-bottom: 1px solid #ecf0f1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.canvas-header h3 {
  color: #2c3e50;
  margin: 0;
  font-size: 18px;
}

.canvas-controls {
  display: flex;
  gap: 10px;
}

.canvas-container {
  flex: 1;
  position: relative;
  background: #f8f9fa;
}

.empty-canvas {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background: #fdfdfd;
}

.empty-canvas-content {
  text-align: center;
  max-width: 400px;
  padding: 40px 20px;
}

.empty-canvas-content i {
  font-size: 48px;
  color: #bdc3c7;
  margin-bottom: 20px;
}

.empty-canvas-content h4 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 20px;
}

.empty-canvas-content p {
  color: #7f8c8d;
  margin-bottom: 25px;
  line-height: 1.5;
}

.canvas-tips {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 13px;
  color: #6c757d;
  text-align: left;
}

.tip i {
  color: #3498db;
  font-size: 14px;
}

.vue-flow-canvas {
  height: 100%;
  width: 100%;
}

/* Button styling */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  font-size: 14px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}

.btn-outline {
  background: transparent;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.btn-outline:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #adb5bd;
}

/* Responsive */
@media (max-width: 1024px) {
  .designer-container {
    flex-direction: column;
    height: auto;
  }
  
  .tools-panel {
    width: 100%;
    max-height: 300px;
  }
  
  .workflow-canvas {
    height: 500px;
  }
}

@media (max-width: 768px) {
  .header-section {
    padding: 15px;
  }
  
  .benefits-grid {
    grid-template-columns: 1fr;
  }
  
  .canvas-controls {
    flex-direction: column;
    gap: 5px;
  }
}
</style> 